/**
 * Termos de marca, concorrente e tema: uma LISTA de expressões booleanas
 * separadas por `;`, que a esteira avalia uma a uma (basta uma casar —
 * `elt/silver/application/brand_matching.py`, `theme_expression.py`).
 *
 * O que se GRAVA continua sendo a string com `;` dentro (é como o banco
 * guarda e a esteira lê); o que se VÊ e se EDITA é um chip por
 * expressão, como o `HorizontalTagInput` da v1 (decisão da dona,
 * 2026-10-09: "igual é na v1").
 *
 * O editor é controlado pela string: `value` entra, `onChange(string)`
 * sai a cada tecla — inclusive com o que ainda está sendo digitado,
 * trimado, como última expressão. Assim quem lê o estado do formulário
 * (o botão "Cadastrar", o `disabled` do concorrente) nunca perde o que
 * a pessoa escreveu sem teclar Enter. Enter, `;` digitado e blur viram
 * o rascunho em chip; Backspace com a caixa vazia tira o último chip.
 *
 * O `<input>` vem ANTES da lista de chips de propósito: os formulários
 * de Ajustes embrulham o campo num `<label>`, que associa o primeiro
 * controle descendente — e o × de cada chip é um `<button>`.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { splitEntries } from "@/shared/text/split-entries";

/** O separador de armazenamento, o mesmo que o servidor e a esteira dividem. */
export const TERMS_SEPARATOR = ";";

export function joinTerms(chips: readonly string[], draft = ""): string {
  const pending = draft.trim();
  return [...chips, ...(pending ? [pending] : [])].join(TERMS_SEPARATOR);
}

type TermsEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

export function TermsEditor({ value, onChange, placeholder, disabled }: TermsEditorProps) {
  const [chips, setChips] = useState<string[]>(() => splitEntries(value));
  const [draft, setDraft] = useState("");
  // O último valor que ESTE editor emitiu: quando `value` chega
  // diferente dele, foi o formulário que trocou (abriu outra marca pra
  // editar, limpou depois de salvar), e os chips se refazem do zero.
  const emitted = useRef(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setChips(splitEntries(value));
    setDraft("");
  }, [value]);

  const emit = (nextChips: string[], nextDraft: string) => {
    setChips(nextChips);
    setDraft(nextDraft);
    const next = joinTerms(nextChips, nextDraft);
    emitted.current = next;
    onChange(next);
  };

  const merge = (current: string[], added: string[]) => [
    ...current,
    ...added.filter(term => !current.includes(term))
  ];

  const commitDraft = (current: string[], raw: string) => emit(merge(current, splitEntries(raw)), "");

  const onInput = (raw: string) => {
    if (!raw.includes(TERMS_SEPARATOR)) {
      emit(chips, raw);
      return;
    }
    // `;` digitado fecha a expressão em chip; o que vem depois dele é
    // o rascunho novo (colar `a; b; c` vira dois chips e "c" na caixa).
    const parts = raw.split(TERMS_SEPARATOR);
    const rest = parts.pop() ?? "";
    emit(merge(chips, splitEntries(parts.join(TERMS_SEPARATOR))), rest);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (draft.trim()) commitDraft(chips, draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && chips.length > 0) {
      event.preventDefault();
      emit(chips.slice(0, -1), "");
    }
  };

  const remove = (term: string) => emit(chips.filter(t => t !== term), draft);

  return (
    <div className="terms-editor">
      <input
        value={draft}
        onChange={e => onInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (draft.trim()) commitDraft(chips, draft);
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
      {chips.length > 0 && (
        <div className="tag-pill-list">
          {chips.map(term => (
            <span key={term} className="tag-pill">
              {term}
              <button
                type="button"
                aria-label={`Remover ${term}`}
                title="Remover"
                onClick={() => remove(term)}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Os termos no CARD, só leitura: um chip por expressão, no estilo dos
 *  apelidos de canal e veículo. */
export function TermChips({ terms, empty = "Sem termos cadastrados" }: { terms: string | null | undefined; empty?: string }) {
  const chips = terms ? splitEntries(terms) : [];
  if (chips.length === 0) return <span>{empty}</span>;
  return (
    <div className="alias-chips">
      {chips.map(term => (
        <span key={term} className="alias-chip">
          {term}
        </span>
      ))}
    </div>
  );
}
